export interface IncomeFormBusinessKey {
    key: string;
    title: string;
    type: 'amount' | 'text';
    order: number;
    categories?: string[];
    commonParticulars?: string[];
    excludeCommonParticulars?: string[];
    /** Split amount rows into dedicated return-view tables. */
    group?: 'expenses' | 'balanceSheet';
}

export const BUSINESS_CATEGORY_KEY = 'businessCatg';
export const BUSINESS_TYPE_KEY = 'businessTypeName';
export const BUSINESS_NAME_KEY = 'businessNm';
export const BUSINESS_ADDRESS_KEY = 'businessAddress';
export const COMMON_PARTICULARS_KEY = 'commonParticulars';
export const FAILURE_OF_DEDUCTION_LIST_KEY = 'failureOfDeductionList';
export const FAILURE_OF_DEDUCTION_TYPE = 'failure_of_deduction_56';
export const ADJUSTMENT_46_LIST_KEY = 'specialBusinessAdjustmentList';
export const ADJUSTMENT_46_TYPE = 'special_bus_any_adjustment46';

export const STANDARD_BUSINESS_CATEGORIES = [
    'REG_BUS_INCOME',
    'INCOME_FROM_AGRO_BUSINESS',
    'EXEMPT_TURNOVER_TAX',
    'CARBONATED_SWEETENED_BEVERAGE',
    'TOBACCO_BUS',
    'BSMT_CONTRACT_OR_SUPPLY',
    'BSMT_SERVICE',
    'BSMT_ADVERTISEMENT',
    'BSMT_COMM_DISCOUNT_FEE',
    'BSMT_TRAVEL_AGENT_COMM',
    'BSMT_FREIGHT_FORWARD_AGENCY_COMM',
    'BSMT_AGENCY_FOREIGN_BUYER',
    'BSMT_IMPORT',
    'BSMT_EXP_OF_MAN',
    'BSMT_C_F_AGENCY',
    'BSMT_EXPORT',
    'BSMT_FOREIGN_INCOME_AGENT',
    'BSMT_REAL_EST_OR_LAND',
    'BSMT_SHIP_BUS',
    'BSMT_COMMERCIAL_MOTOR_VEHICLE',
    'BSMT_INLAND_SHIPPING',
    'BSMT_OTHERS',
    'EXEMPT_OR_REDUCED_SRO',
];

/** Categories that show TDS fields in the main business form (plus REG_BUS_INCOME only for MANUFACTURING_WITH_TDS). */
export const BUSINESS_TDS_CATEGORIES = [
    'REG_BUS_INCOME',
    'BSMT_CONTRACT_OR_SUPPLY',
    'BSMT_SERVICE',
    'BSMT_ADVERTISEMENT',
    'BSMT_COMM_DISCOUNT_FEE',
    'BSMT_TRAVEL_AGENT_COMM',
    'BSMT_FREIGHT_FORWARD_AGENCY_COMM',
    'BSMT_AGENCY_FOREIGN_BUYER',
    'BSMT_IMPORT',
    'BSMT_EXP_OF_MAN',
    'BSMT_C_F_AGENCY',
    'BSMT_EXPORT',
    'BSMT_FOREIGN_INCOME_AGENT',
    'BSMT_REAL_EST_OR_LAND',
    'BSMT_SHIP_BUS',
    'BSMT_COMMERCIAL_MOTOR_VEHICLE',
    'BSMT_INLAND_SHIPPING',
    'BSMT_OTHERS',
];

export const SPECIAL_BUSINESS_SALE_TYPES = [
    'special_bus_sale_capital_asset',
    'special_bus_insurance_salvage_comp',
];

/** Categories that show Balance Sheet Summary when books of accounts are maintained. */
export const BALANCE_SHEET_CATEGORIES = STANDARD_BUSINESS_CATEGORIES.concat(['TEA_RUB']);

export const INCOME_FORM_BUSINESS_KEYS: IncomeFormBusinessKey[] = [
    { key: BUSINESS_CATEGORY_KEY, title: 'Business Category', type: 'text', order: 1 },
    { key: BUSINESS_TYPE_KEY, title: 'Business Type', type: 'text', order: 2 },
    { key: BUSINESS_NAME_KEY, title: 'Name of the Business', type: 'text', order: 3 },
    { key: BUSINESS_ADDRESS_KEY, title: 'Address of the Business', type: 'text', order: 4 },
    { key: COMMON_PARTICULARS_KEY, title: 'Income Type', type: 'text', order: 5, categories: ['SPECIAL_BUS_INCOME'] },
    { key: 'teaRubberCultivationArea', title: 'Total Cultivation Area', type: 'text', order: 6, categories: ['TEA_RUB'] },
    { key: 'unitOfTotalCulArea', title: 'Unit of Cultivation Area', type: 'text', order: 7, categories: ['TEA_RUB'] },
    { key: 'paymentDate', title: 'Payment Date', type: 'text', order: 8, categories: ['BUS_INCOME_FROM_ROYALTY_INTANGIBLES'] },
    { key: 'sroNoRef', title: 'SRO No./Reference', type: 'text', order: 9, categories: ['EXEMPT_OR_REDUCED_SRO'] },
    { key: 'sroYear', title: 'Year', type: 'text', order: 10, categories: ['EXEMPT_OR_REDUCED_SRO'] },
    { key: COMMON_PARTICULARS_KEY, title: 'Particular', type: 'text', order: 11, categories: ['EXEMPT_OR_REDUCED_SRO'] },
    { key: 'tdsRelevant', title: 'Relevant Section of TDS', type: 'text', order: 12, categories: ['BSMT_OTHERS'] },

    { key: 'salesTurnReceipts', title: 'Sales/Turnover/Receipts', type: 'amount', order: 12, categories: STANDARD_BUSINESS_CATEGORIES },
    { key: 'salesTurnReceipts', title: 'Sale proceed (Total)', type: 'amount', order: 12, categories: ['TEA_RUB'] },
    { key: 'salesTurnReceipts', title: 'Sales Proceed/Receipt', type: 'amount', order: 12, categories: ['SPECIAL_BUS_INCOME'], commonParticulars: SPECIAL_BUSINESS_SALE_TYPES },

    { key: 'costOfGoodsSold', title: 'Cost of Production/Cost of Goods Sold', type: 'amount', order: 13, categories: STANDARD_BUSINESS_CATEGORIES },
    { key: 'grossProfit', title: 'Gross Profit', type: 'amount', order: 14, categories: STANDARD_BUSINESS_CATEGORIES },

    { key: 'contributionToWWF', title: "Contribution to Worker's Welfare Fund", type: 'amount', order: 15, categories: STANDARD_BUSINESS_CATEGORIES, group: 'expenses' },
    { key: 'depreciation', title: 'Depreciation', type: 'amount', order: 16, categories: STANDARD_BUSINESS_CATEGORIES, group: 'expenses' },
    { key: 'amortization', title: 'Amortization', type: 'amount', order: 17, categories: STANDARD_BUSINESS_CATEGORIES, group: 'expenses' },
    { key: 'rndExpenseAmount', title: 'R&D expense', type: 'amount', order: 18, categories: STANDARD_BUSINESS_CATEGORIES, group: 'expenses' },
    { key: 'financialExpenseAmount', title: 'Financial expense (Bank/FI interest, other charges & related expenses)', type: 'amount', order: 19, categories: STANDARD_BUSINESS_CATEGORIES, group: 'expenses' },
    { key: 'badDebtWrittenOffAmount', title: 'Bad debt written off', type: 'amount', order: 20, categories: STANDARD_BUSINESS_CATEGORIES, group: 'expenses' },
    { key: 'amountPaidForRightOfUse', title: 'Amount paid for Right of Use', type: 'amount', order: 21, categories: STANDARD_BUSINESS_CATEGORIES, group: 'expenses' },
    { key: 'otherExpenseAmount', title: 'All general, administrative, selling & other expenses (Consolidated)', type: 'amount', order: 22, categories: STANDARD_BUSINESS_CATEGORIES.concat(['BUS_INCOME_FROM_ROYALTY_INTANGIBLES']), group: 'expenses' },
    { key: 'gasoExpenses', title: 'Total general, administrative, selling & other expenses', type: 'amount', order: 23, categories: STANDARD_BUSINESS_CATEGORIES },
    { key: 'netProfit', title: 'Net Profit', type: 'amount', order: 24, categories: STANDARD_BUSINESS_CATEGORIES.concat(['TEA_RUB']) },
    { key: 'taxExemptedIncm', title: 'Tax Exempted Income', type: 'amount', order: 25, categories: ['BSMT_EXPORT', 'EXEMPT_OR_REDUCED_SRO'] },
    { key: 'taxableIncm', title: 'Taxable Income', type: 'amount', order: 26, categories: ['BSMT_EXPORT', 'EXEMPT_OR_REDUCED_SRO'] },
    { key: 'taxAppl', title: 'Tax Applicable', type: 'amount', order: 27, categories: ['EXEMPT_OR_REDUCED_SRO'] },
    { key: 'tds', title: 'Tax Deducted/Collected at Source', type: 'amount', order: 28, categories: BUSINESS_TDS_CATEGORIES },
    { key: 'tds120', title: 'Tax Deducted/Collected at Source (U/S 120)', type: 'amount', order: 29, categories: ['BSMT_CONTRACT_OR_SUPPLY', 'BSMT_EXPORT'] },

    { key: 'specialBusinessCostAcquisition', title: 'Cost of Acquisition', type: 'amount', order: 30, categories: ['SPECIAL_BUS_INCOME'], commonParticulars: SPECIAL_BUSINESS_SALE_TYPES },
    { key: 'specialBusinessWrittenValue', title: 'Written Down Value', type: 'amount', order: 31, categories: ['SPECIAL_BUS_INCOME'], commonParticulars: SPECIAL_BUSINESS_SALE_TYPES },
    { key: 'specialBusinessIncome', title: 'Business Income', type: 'amount', order: 32, categories: ['SPECIAL_BUS_INCOME'], commonParticulars: SPECIAL_BUSINESS_SALE_TYPES },
    { key: 'specialBusinessCapitalGain', title: 'Capital Gain', type: 'amount', order: 33, categories: ['SPECIAL_BUS_INCOME'], commonParticulars: SPECIAL_BUSINESS_SALE_TYPES },

    { key: 'teaRubberSales', title: 'Sales Proceed', type: 'amount', order: 34, categories: ['TEA_RUB'] },
    { key: 'teaRubberProdCost', title: 'Cost of Production', type: 'amount', order: 35, categories: ['TEA_RUB'] },
    { key: 'teaRubberOtherDeduct', title: 'Other Allowable Deduction', type: 'amount', order: 36, categories: ['TEA_RUB'] },
    { key: 'teaRubberNetIncm', title: 'Net Income', type: 'amount', order: 37, categories: ['TEA_RUB'] },
    { key: 'teaRubberAgrIncm', title: 'Agricultural Income', type: 'amount', order: 38, categories: ['TEA_RUB'] },
    { key: 'teaRubberIncmBusProf', title: 'Business Income', type: 'amount', order: 39, categories: ['TEA_RUB'] },

    { key: 'maintainBooksOfAccounts', title: 'Maintain Books of Accounts', type: 'text', order: 39.5, categories: BALANCE_SHEET_CATEGORIES },

    { key: 'cashHandBank', title: 'Cash in Hand & at Bank', type: 'amount', order: 40, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'inventories', title: 'Inventories', type: 'amount', order: 41, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'fixedAssets', title: 'Fixed Assets', type: 'amount', order: 42, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'otherAssets', title: 'Other Assets', type: 'amount', order: 43, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'totalAssets', title: 'Total Assets', type: 'amount', order: 44, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'openingCp', title: 'Opening Capital & New Investment This Year', type: 'amount', order: 45, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'netProfit', title: 'Net Profit', type: 'amount', order: 46, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'withdrawalsIncmYr', title: 'Withdrawals in the Income Year', type: 'amount', order: 47, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'closingCp', title: 'Closing Capital', type: 'amount', order: 48, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'liabilities', title: 'Liabilities', type: 'amount', order: 49, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },
    { key: 'totalCpLb', title: 'Total Capital and Liabilities', type: 'amount', order: 50, categories: BALANCE_SHEET_CATEGORIES, group: 'balanceSheet' },

    { key: 'grossProfit', title: 'Gross Receipt', type: 'amount', order: 12, categories: ['BUS_INCOME_FROM_ROYALTY_INTANGIBLES'] },
];

export const BUSINESS_CATEGORY_LABELS: Record<string, string> = {
    REG_BUS_INCOME: 'Business or Professional Income',
    INCOME_FROM_AGRO_BUSINESS: 'Certain Sources of Agro-Business Income',
    BSMT_CONTRACT_OR_SUPPLY: 'Contract / Supply of Goods (TDS U/S 89)',
    BSMT_SERVICE: 'Profession / Consultancy / Other Services (TDS U/S 90)',
    BSMT_ADVERTISEMENT: 'Advertisement (TDS U/S 92)',
    BSMT_COMM_DISCOUNT_FEE: 'Commission, Discount fee (TDS U/S 94)',
    BSMT_TRAVEL_AGENT_COMM: 'Travel Agent Commission (TDS U/S 95)',
    BSMT_FREIGHT_FORWARD_AGENCY_COMM: 'Convention hall/ Conference center service (TDS U/S 110)',
    BSMT_AGENCY_FOREIGN_BUYER: 'Agency of Foreign Buyer (TDS U/S 116)',
    BSMT_IMPORT: 'Import (TDS U/S 120)',
    BSMT_EXP_OF_MAN: 'Export of Manpower (TDS U/S 121)',
    BSMT_C_F_AGENCY: 'C & F Agency (TDS U/S 122)',
    BSMT_EXPORT: 'Export (TDS U/S 123)',
    BSMT_FOREIGN_INCOME_AGENT: 'Foreign Income Agent Services (TDS U/S 124)',
    BSMT_REAL_EST_OR_LAND: 'Real State or Land Development Business (TDS U/S 126)',
    BSMT_SHIP_BUS: 'Shipping Business (TDS U/S 132)',
    BSMT_COMMERCIAL_MOTOR_VEHICLE: 'Commercial Motor Vehicle (TDS U/S 138)',
    BSMT_INLAND_SHIPPING: 'Inland Shipping (TDS U/S 139)',
    BSMT_OTHERS: 'Any other Business / Service / Profession (with TDS)',
    TOBACCO_BUS: 'Manufacturing of Tobacco Products',
    TEA_RUB: 'Production of Tea or Rubber',
    EXEMPT_OR_REDUCED_SRO: 'Income Subject to Reduced Tax Rate',
    SPECIAL_BUS_INCOME: 'Special Business Income',
    CARBONATED_SWEETENED_BEVERAGE: 'Manufacturing of Carbonated/ Sweetened Beverage',
    EXEMPT_TURNOVER_TAX: 'Business Exempted from Turnover Tax',
    BUS_INCOME_FROM_ROYALTY_INTANGIBLES: 'Business Income from Royalty, Intangibles etc.',
};

export const COMMON_PARTICULARS_LABELS: Record<string, string> = {
    special_bus_sale_capital_asset: 'Sale of Capital Asset',
    special_bus_insurance_salvage_comp: 'Insurance, Salvage or Compensation Receipt',
    special_bus_any_adjustment46: 'Other Income/Adjustment U/S 46',
    special_bus_any_adjustment: 'Any other adjustment [U/S 46 & 56]',
    failure_of_deduction_56: 'Failure of deduction, collection or Payment of Source Tax U/S 56',
};

/** Dropdown value → display title for businessTypeName (all categories). */
export const BUSINESS_TYPE_LABELS: Record<string, string> = {
    // REG_BUS_INCOME – Business (Regular)
    MANUFACTURING: 'Manufacturing',
    MANUFACTURING_WITH_TDS: 'Manufacturing with TDS on raw material import',
    TRADING: 'Trading',
    SERVICE: 'Service',
    OTHERS: 'Others',
    // REG_BUS_INCOME – Profession (Without TDS)
    DOCTOR_MEDICAL_PROFESSION: 'Doctor/Medical Profession',
    LAWYER_LEGAL_CONSULTANT: 'Lawyer/Legal Consultant',
    VETERINARY_LICENSED_STOCK_PROFESSIONAL: 'Veterinary/Livestock Professional',
    VETERINARY_LIVESTOCK_PROFESSION: 'Veterinary/Livestock Profession',
    ARTIST: 'Artist',
    ARCHITECT: 'Architect',
    ENGINEER: 'Engineer',
    CA_CMA: 'CA/CMA',
    IT_PROFESSIONAL: 'IT Professional',
    IT_PROFESSION: 'IT Profession',
    OTHER_PROFESSION_CONSULTANT: 'Other Profession/Consultant',
    // INCOME_FROM_AGRO_BUSINESS
    Dairy_Food_Production: 'Dairy Food Production',
    Frog_Farming: 'Frog Production',
    Pelleted_Poultry_Feed_Production: 'Pelleted Poultry Feed Production',
    Pelleted_Shrimp_Fish_Feed_Production: 'Pelleted Shrimp & Fish Feed Production',
    Poultry_Shrimp_Fish_Hatchery: 'Poultry, Shrimp & Fish Hatchery',
    Seed_Marketing: 'Seed Marketing',
    Sericulture: 'Sericulture',
    // EXEMPT_TURNOVER_TAX
    Commission_Business: 'Commission Business',
    DO_Business: 'DO (Delivery Order) Business',
    Money_Exchange_Business: 'Money Exchange Business',
    Commission_DO_Money_Exchange_Consolidate: 'Commission/ DO/ Money Exchange (Consolidated)',
    Sale_Purchase_Business_of_gold_silver_jewelry_etc: 'Sale/ Purchase Business of gold, silver, jewelry etc.',
    // BSMT_IMPORT (Trading/Manufacturing already covered)
    // BSMT_CONTRACT_OR_SUPPLY
    CONTRACT_SUPPLY: 'Contract/Supply',
    PRODUCTION_PROCESSING_CONVERSION: 'Manufacture, Process or Conversion',
    PRINTING_PACKAGING_BINDING: 'Printing, Packaging or Binding',
    SUPPLY_OF_IMPORTED_ITEMS: 'Supply of Imported Items',
    // BSMT_EXPORT
    EXPORT: 'Export',
    // Royalty / intangibles (stored in businessTypeName on rows; also used at parent level)
    Royalty: 'Royalty',
    'License Fee': 'License Fee',
    'Technical Assistance Fee': 'Fees for Technical Services',
    'Intangible Assets': 'Intangible Assets',
};

export const FAILURE_OF_DEDUCTION_COLUMNS = [
    { key: 'headOfExpenses', title: 'Head Of Expenses', isAmount: false },
    { key: 'amount', title: 'Amount', isAmount: true },
    { key: 'tdsAmount', title: 'TDS Applicable Amount', isAmount: true },
    { key: 'tds50Parent', title: '50% of TDS', isAmount: true },
    { key: 'totalPayable', title: 'Total Payable', isAmount: true },
];

export const SPECIAL_BUSINESS_ADJUSTMENT_OPTIONS: Record<string, string> = {
    TRANSFER_OF_EXPORT_QUOTA: 'Transfer of Export Quota',
    BENEFITS_TRADING_LIABILITY: 'Any benefits received in respect of trading Liability.',
    UNPAID_TRADING_LIABILITY: 'Unpaid trading liability.',
    BENEFIT_DEDUCTED_LOSS_BAD_DEBT_EXPENSE: 'Any Benefit Received in respect of deducted loss, bad debt or expense.',
    DEFICIENCY_INITIAL_CAPITAL: 'Any deficiency in the initial capital',
    UNPAID_INTEREST_PROFIT_PREVIOUSLY_ALLOWED: 'Unpaid Interest or Profit Previously Allowed as expense',
};
